import {
	type CoordinateSetting,
	type GridFoundationParams,
	computeGridIntersection,
} from "@earthbrain/foundation-excavation-sdk";

const MM_TO_M = 1 / 1000;

/**
 * Converts SDK drawing coordinates (Y-up, mm) into site coordinates
 * (east/north, m) using the two points of the coordinate setting.
 *
 * A rigid transform - rotation plus translation, no scaling:
 *   - basePoint      = translation anchor (drawing intersection -> eastM/northM)
 *   - directionPoint = orientation, used only to derive the rotation angle theta
 * Axis mapping: drawing x -> East, drawing z -> North, drawing y -> Up
 * (Z = y/1000, with 0 as the reference height).
 *
 * @param vertices SDK drawing coordinates, flat [x,y,z,...] in mm
 * @param grid Grid span definition (snapshot.grid)
 * @param coordinateSetting Site coordinates of the two grid intersections
 * @returns Site coordinates, flat [east, north, Z, ...] in m
 */
export function transformDrawingToSiteEastNorth(
	vertices: ReadonlyArray<number>,
	grid: Readonly<GridFoundationParams>,
	coordinateSetting: Readonly<CoordinateSetting>,
): number[] {
	if (vertices.length === 0) return [];

	const { basePoint, directionPoint } = coordinateSetting;
	const base = computeGridIntersection(grid, basePoint.gridX, basePoint.gridY);
	const dir = computeGridIntersection(
		grid,
		directionPoint.gridX,
		directionPoint.gridY,
	);

	// --- rotation: align drawing direction (base->dir) to field direction (base->dir)
	const drawingAngle = Math.atan2(dir.z - base.z, dir.x - base.x);
	const fieldAngle = Math.atan2(
		directionPoint.northM - basePoint.northM,
		directionPoint.eastM - basePoint.eastM,
	);
	const theta = fieldAngle - drawingAngle;
	const cos = Math.cos(theta);
	const sin = Math.sin(theta);

	// --- apply: relative-to-base (mm->m) -> rotate -> translate to base east/north
	const result: number[] = new Array(vertices.length);
	for (let i = 0; i < vertices.length; i += 3) {
		const lx = (vertices[i] - base.x) * MM_TO_M;
		const lz = (vertices[i + 2] - base.z) * MM_TO_M;
		result[i] = basePoint.eastM + lx * cos - lz * sin;
		result[i + 1] = basePoint.northM + lx * sin + lz * cos;
		result[i + 2] = vertices[i + 1] * MM_TO_M;
	}
	return result;
}
